import { ref } from 'vue'
import { defineStore } from 'pinia'

export const useDictionaryStore = defineStore('dictionary', () => {
  const dictionary = ref(null)
  const isLoading = ref(false)

  const initialize = () => {
    if (isLoading.value) return

    isLoading.value = true

    fetch('/greekDictionaryALL')
      // fetch words file
      .then((fetchResponse) => fetchResponse.text())
      // get file text
      .then((textResponse) => {
        // extract words from text, normalize and add them to dictionary set
        dictionary.value = new Set(
          textResponse.split('\n').map((word) => utils.normalizeWord(word))
        )
      })
      .catch((error) => {
        console.error('Failed to load dictionary:', error)
      })
      .finally((isLoading.value = false))
  }

  const spellCheckWord = (word) => {
    const normalized = utils.normalizeWord(word)
    const isMisspell = !this.dictionary.has(normalized)
    return {
      word,
      isMisspell
    }
  }

  const generateSuggestions = (word) => {
    const suggestions = new Set()
    utils.getEdits(word).forEach((edit) => {
      if (dictionary.value.has(edit)) {
        suggestions.add(edit)
      }
    })
    return Array.from(suggestions)
      .sort((a, b) => utils.levenshteinDistance(word, a) - utils.levenshteinDistance(word, b))
      .slice(0, this.maxSuggestions)
  }

  return {
    initialize,
    dictionary,
    spellCheckWord
  }
})

const utils = {
  normalizeWord: (word) => {
    return word
      .trim()
      .toLowerCase()
      .normalize('NFC')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\r$/, '')
  },
  getEdits: (word) => {
    const edits = new Set()
    const letters = 'αβγδεζηθικλμνξοπρστυφχψωςάέήίόύώϊϋΐΰ'

    // Deletions
    for (let i = 0; i < word.length; i++) {
      edits.add(word.slice(0, i) + word.slice(i + 1))
    }

    // Transpositions
    for (let i = 0; i < word.length - 1; i++) {
      edits.add(word.slice(0, i) + word[i + 1] + word[i] + word.slice(i + 2))
    }

    // Replacements
    for (let i = 0; i < word.length; i++) {
      for (const letter of letters) {
        edits.add(word.slice(0, i) + letter + word.slice(i + 1))
      }
    }

    // Insertions
    for (let i = 0; i <= word.length; i++) {
      for (const letter of letters) {
        edits.add(word.slice(0, i) + letter + word.slice(i))
      }
    }

    return edits
  },
  levenshteinDistance: (a, b) => {
    const matrix = []
    for (let i = 0; i <= b.length; i++) matrix[i] = [i]
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        const cost = b[i - 1] === a[j - 1] ? 0 : 1
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        )
      }
    }

    return matrix[b.length][a.length]
  }
}
